/**
 * HealthMetricsConfigModal Component
 *
 * Modal for editing health metrics configuration from the workflow screen.
 * Allows users to modify which metrics are tracked (source + metrics list).
 * Includes "Apply to future sessions" checkbox to optionally update global settings.
 *
 * Key behaviors:
 * - Manual Save/Cancel (not auto-save) - config changes are significant
 * - Checkbox always visible; default checked if draft matched global at open, unchecked if divergent
 * - "Reset to defaults" button visible when draft was divergent at modal open
 * - Alert shown when draft was divergent at modal open (not triggered by active edits)
 * - Saves to draft config first, optionally to global settings if checkbox checked
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Input,
  NumberInput,
  Checkbox,
  SortableList,
  DragHandle,
  EmptyState,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import { createDefaultMetricsForSource } from '@/config/defaults';
import { sessionDraftService } from '@/services/SessionDraftService';
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { computeHealthMetricsHash } from '../utils/sessionDraftUtils';
import type { PreTradingConfigSnapshot } from '@/types/transient/SessionDraft';
import type { PreTradingSettings } from '@/types/schema/PreTradingSettings';

// ============================================================================
// Types
// ============================================================================

/**
 * Health metric with required id for SortableList
 */
interface HealthMetric {
  id: string;
  name: string;
  min: number;
  max: number;
  unit?: string;
}

/**
 * Source options for health data
 */
type HealthMetricSource = 'apple' | 'whoop' | 'oura' | 'garmin' | 'custom';

const SOURCE_OPTIONS: Array<{ key: HealthMetricSource; label: string }> = [
  { key: 'apple', label: 'Apple Watch' },
  { key: 'whoop', label: 'Whoop' },
  { key: 'oura', label: 'Oura' },
  { key: 'garmin', label: 'Garmin' },
  { key: 'custom', label: 'Custom' },
];

/**
 * Validation error state for a single metric
 */
interface MetricValidationErrors {
  name?: string;
  min?: string;
  max?: string;
}

export interface HealthMetricsConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Session draft ID */
  draftId: string;
  /** Current draft config snapshot */
  draftConfig: PreTradingConfigSnapshot;
  /** Current global settings (should be fresh when modal opens) */
  currentSettings: PreTradingSettings;
  /** Callback on successful save with updated config and migrated values */
  onSaveSuccess: (
    updatedConfig: PreTradingConfigSnapshot,
    migratedHealthMetrics: Record<string, number> | undefined
  ) => void;
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate a single metric
 */
function validateMetric(
  metric: HealthMetric,
  allMetrics: HealthMetric[]
): MetricValidationErrors {
  const errors: MetricValidationErrors = {};
  const trimmedName = metric.name.trim();

  if (!trimmedName) {
    errors.name = 'Name is required';
  } else {
    const isDuplicate = allMetrics.some(
      (m) =>
        m.id !== metric.id &&
        m.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      errors.name = 'Name must be unique';
    }
  }

  if (isNaN(metric.min)) {
    errors.min = 'Min is required';
  }

  if (isNaN(metric.max)) {
    errors.max = 'Max is required';
  } else if (!isNaN(metric.min) && metric.max <= metric.min) {
    errors.max = 'Max must be greater than min';
  }

  return errors;
}

/**
 * Check if all metrics are valid
 */
function areAllMetricsValid(metrics: HealthMetric[]): boolean {
  return metrics.every(
    (metric) => Object.keys(validateMetric(metric, metrics)).length === 0
  );
}

/**
 * Compare local health metrics against current settings to detect divergence.
 * Used to determine checkbox vs "Reset to defaults" visibility.
 */
function isHealthMetricsDivergent(
  localSource: HealthMetricSource,
  localMetrics: HealthMetric[],
  currentSettings: PreTradingSettings
): boolean {
  const currentHealthMetrics = currentSettings.healthMetrics;

  // Source change = divergent
  if (localSource !== currentHealthMetrics.source) {
    return true;
  }

  const currentList = currentHealthMetrics.metrics;

  // Different count = divergent
  if (localMetrics.length !== currentList.length) {
    return true;
  }

  // Build map of current metrics by id
  const currentById = new Map(currentList.map((m) => [m.id, m]));

  // Check each local metric exists in current with same properties
  for (const localMetric of localMetrics) {
    const currentMetric = currentById.get(localMetric.id);
    if (!currentMetric) {
      return true;
    }
    if (
      localMetric.name !== currentMetric.name ||
      localMetric.min !== currentMetric.min ||
      localMetric.max !== currentMetric.max ||
      localMetric.unit !== currentMetric.unit
    ) {
      return true;
    }
  }

  return false;
}

// ============================================================================
// Component
// ============================================================================

export const HealthMetricsConfigModal: React.FC<HealthMetricsConfigModalProps> = ({
  isOpen,
  onClose,
  draftId,
  draftConfig,
  currentSettings,
  onSaveSuccess,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localSource, setLocalSource] = useState<HealthMetricSource>('custom');
  const [localMetrics, setLocalMetrics] = useState<HealthMetric[]>([]);
  const [applyToFuture, setApplyToFuture] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, MetricValidationErrors>
  >({});

  // Source change confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSource, setPendingSource] = useState<HealthMetricSource | null>(null);

  // Mobile edit modal state
  const [editingMetricId, setEditingMetricId] = useState<string | 'new' | null>(null);
  const [editingDraft, setEditingDraft] = useState<HealthMetric | null>(null);
  const [editModalErrors, setEditModalErrors] = useState<MetricValidationErrors>({});

  // Track whether draft was divergent from global when modal opened (snapshot, not live)
  const [wasInitiallyDivergent, setWasInitiallyDivergent] = useState(false);

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Initialize local state from draftConfig when modal opens
  // Guard ensures we only initialize on open transition, not when props change while open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setLocalSource(draftConfig.healthMetrics.source as HealthMetricSource);
      setLocalMetrics(draftConfig.healthMetrics.metrics.map((m) => ({ ...m })));
      setValidationErrors({});
      setIsSaving(false);

      // Compute initial divergence from draftConfig vs currentSettings (not local state)
      const initiallyDivergent = isHealthMetricsDivergent(
        draftConfig.healthMetrics.source as HealthMetricSource,
        draftConfig.healthMetrics.metrics,
        currentSettings
      );
      setWasInitiallyDivergent(initiallyDivergent);

      // Checkbox default: checked if draft matched global at open, unchecked if divergent
      setApplyToFuture(!initiallyDivergent);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, draftConfig, currentSettings]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateAllMetrics = useCallback((metricsToValidate: HealthMetric[]) => {
    const errors: Record<string, MetricValidationErrors> = {};
    metricsToValidate.forEach((metric) => {
      const metricErrors = validateMetric(metric, metricsToValidate);
      if (Object.keys(metricErrors).length > 0) {
        errors[metric.id] = metricErrors;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  // ============================================================================
  // Source Selection
  // ============================================================================

  const handleSourceSelection = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') return;
      const selectedKey = Array.from(keys)[0] as HealthMetricSource | undefined;
      if (!selectedKey || selectedKey === localSource) return;

      // If no metrics exist, apply immediately
      if (localMetrics.length === 0) {
        const newMetrics = createDefaultMetricsForSource(selectedKey) as HealthMetric[];
        setLocalSource(selectedKey);
        setLocalMetrics(newMetrics);
        setValidationErrors({});
        return;
      }

      // Show confirmation dialog
      setPendingSource(selectedKey);
      setShowConfirmModal(true);
    },
    [localSource, localMetrics.length]
  );

  const handleConfirmSourceChange = useCallback(() => {
    if (pendingSource) {
      const newMetrics = createDefaultMetricsForSource(pendingSource) as HealthMetric[];
      setLocalSource(pendingSource);
      setLocalMetrics(newMetrics);
      setValidationErrors({});
    }
    setShowConfirmModal(false);
    setPendingSource(null);
  }, [pendingSource]);

  const handleCancelSourceChange = useCallback(() => {
    setShowConfirmModal(false);
    setPendingSource(null);
  }, []);

  // ============================================================================
  // Metric CRUD (Desktop Inline)
  // ============================================================================

  const handleMetricChange = useCallback(
    (id: string, field: keyof HealthMetric, value: string | number) => {
      setLocalMetrics((prev) => {
        const updated = prev.map((m) =>
          m.id === id ? { ...m, [field]: value } : m
        );
        validateAllMetrics(updated);
        return updated;
      });
    },
    [validateAllMetrics]
  );

  const handleAddMetric = useCallback(() => {
    const newMetric: HealthMetric = {
      id: generateUUID(),
      name: '',
      min: 0,
      max: 100,
      unit: '',
    };
    setLocalMetrics((prev) => {
      const updated = [...prev, newMetric];
      validateAllMetrics(updated);
      return updated;
    });
  }, [validateAllMetrics]);

  const handleDeleteMetric = useCallback(
    (id: string) => {
      setLocalMetrics((prev) => {
        const updated = prev.filter((m) => m.id !== id);
        setValidationErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[id];
          return newErrors;
        });
        validateAllMetrics(updated);
        return updated;
      });
    },
    [validateAllMetrics]
  );

  const handleReorder = useCallback(
    (reorderedMetrics: HealthMetric[]) => {
      setLocalMetrics(reorderedMetrics);
      validateAllMetrics(reorderedMetrics);
    },
    [validateAllMetrics]
  );

  // ============================================================================
  // Mobile Edit Modal
  // ============================================================================

  const handleOpenEditModal = useCallback((metric: HealthMetric) => {
    setEditingMetricId(metric.id);
    setEditingDraft({ ...metric });
    setEditModalErrors({});
  }, []);

  const handleOpenAddModal = useCallback(() => {
    const newMetric: HealthMetric = {
      id: generateUUID(),
      name: '',
      min: 0,
      max: 100,
      unit: '',
    };
    setEditingMetricId('new');
    setEditingDraft(newMetric);
    setEditModalErrors({});
  }, []);

  const handleEditDraftChange = useCallback(
    (field: keyof HealthMetric, value: string | number) => {
      setEditingDraft((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, [field]: value };
        const otherMetrics =
          editingMetricId === 'new'
            ? localMetrics
            : localMetrics.filter((m) => m.id !== editingMetricId);
        const errors = validateMetric(updated, [...otherMetrics, updated]);
        setEditModalErrors(errors);
        return updated;
      });
    },
    [editingMetricId, localMetrics]
  );

  const handleSaveEditModal = useCallback(() => {
    if (!editingDraft) return;

    const otherMetrics =
      editingMetricId === 'new'
        ? localMetrics
        : localMetrics.filter((m) => m.id !== editingMetricId);
    const errors = validateMetric(editingDraft, [...otherMetrics, editingDraft]);

    if (Object.keys(errors).length > 0) {
      setEditModalErrors(errors);
      return;
    }

    let updated: HealthMetric[];
    if (editingMetricId === 'new') {
      updated = [...localMetrics, editingDraft];
    } else {
      updated = localMetrics.map((m) =>
        m.id === editingMetricId ? editingDraft : m
      );
    }

    setLocalMetrics(updated);
    validateAllMetrics(updated);
    setEditingMetricId(null);
    setEditingDraft(null);
    setEditModalErrors({});
  }, [editingDraft, editingMetricId, localMetrics, validateAllMetrics]);

  const handleCancelEditModal = useCallback(() => {
    setEditingMetricId(null);
    setEditingDraft(null);
    setEditModalErrors({});
  }, []);

  // ============================================================================
  // Reset to Defaults
  // ============================================================================

  const handleResetToDefaults = useCallback(() => {
    setLocalSource(currentSettings.healthMetrics.source as HealthMetricSource);
    setLocalMetrics(currentSettings.healthMetrics.metrics.map((m) => ({ ...m })));
    setValidationErrors({});
    // Set checkbox to checked after reset (user explicitly chose to match defaults)
    setApplyToFuture(true);
    // Clear divergence warning since user explicitly chose to match defaults
    setWasInitiallyDivergent(false);
  }, [currentSettings]);

  // ============================================================================
  // Save Handler
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Guard: currentSettings must be available
    if (!currentSettings) {
      showToast({
        title: 'Cannot save',
        description: 'Settings are not available. Please try again.',
        color: 'danger',
      });
      return;
    }

    // Validate all metrics
    if (!areAllMetricsValid(localMetrics)) {
      validateAllMetrics(localMetrics);
      showToast({
        title: 'Validation errors',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Build healthMetricsSettings from local state
      // Use exactOptionalPropertyTypes-safe mapping (only include unit if defined)
      const healthMetricsSettings: PreTradingSettings['healthMetrics'] = {
        source: localSource,
        metrics: localMetrics.map((m) => {
          const metric: PreTradingSettings['healthMetrics']['metrics'][number] = {
            id: m.id,
            name: m.name,
            min: m.min,
            max: m.max,
          };
          if (m.unit) {
            metric.unit = m.unit;
          }
          return metric;
        }),
      };

      // Step 1: Update draft config
      const draftSuccess = await sessionDraftService.updateHealthMetricsConfig(
        draftId,
        healthMetricsSettings
      );

      if (!draftSuccess) {
        showToast({
          title: 'Save failed',
          description: 'Session draft not found or invalid.',
          color: 'danger',
        });
        setIsSaving(false);
        return;
      }

      // Step 2: If checkbox checked, update global settings
      let globalUpdateSucceeded = true;
      if (applyToFuture) {
        try {
          await preTradingSettingsService.update(currentSettings.id, {
            healthMetrics: healthMetricsSettings,
          });
        } catch (err) {
          console.error('Failed to update global settings:', err);
          globalUpdateSucceeded = false;
          showToast({
            title: 'Partial save',
            description:
              "Changes saved to current session, but couldn't update defaults for future sessions.",
            color: 'warning',
          });
        }
      }

      // Step 3: Ack hash logic
      if (applyToFuture) {
        if (globalUpdateSucceeded) {
          // Global update succeeded - hash the new global settings
          const newGlobalSettings: PreTradingSettings = {
            ...currentSettings,
            healthMetrics: healthMetricsSettings,
          };
          const ackHash = computeHealthMetricsHash(newGlobalSettings);
          await sessionDraftService.setHealthMetricsAckHash(draftId, ackHash);
        }
        // If global update failed, do NOT set ack hash, keep modal open
        if (!globalUpdateSucceeded) {
          setIsSaving(false);
          return; // Keep modal open for retry
        }
      } else {
        // Checkbox unchecked - set ack hash to current global (intentional divergence)
        const ackHash = computeHealthMetricsHash(currentSettings);
        await sessionDraftService.setHealthMetricsAckHash(draftId, ackHash);
      }

      // Step 4: Re-fetch updated draft to get migrated values
      const updatedDraft = await sessionDraftService.getById(draftId);
      if (updatedDraft && updatedDraft.preTradingConfig) {
        onSaveSuccess(
          updatedDraft.preTradingConfig,
          updatedDraft.healthMetrics
        );
      }

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    currentSettings,
    localSource,
    localMetrics,
    applyToFuture,
    draftId,
    validateAllMetrics,
    showToast,
    onSaveSuccess,
    onClose,
  ]);

  // ============================================================================
  // Metric Row Renderer
  // ============================================================================

  const renderMetricRow = useCallback(
    (metric: HealthMetric, dragHandleProps: DragHandleProps) => {
      const errors = validationErrors[metric.id] || {};
      const hasError = Object.keys(errors).length > 0;
      const rangeDisplay = `${metric.min}–${metric.max}${metric.unit ? ` ${metric.unit}` : ''}`;

      return (
        <div
          className={`
            rounded-lg bg-default-50
            ${dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
          `}
        >
          {/* Mobile Layout */}
          <div className="md:hidden flex items-center gap-3 p-3">
            <DragHandle dragHandleProps={dragHandleProps} />
            <button
              type="button"
              className="flex-1 text-left"
              onClick={() => handleOpenEditModal(metric)}
            >
              <div className="flex items-center gap-2">
                <span className={`font-medium ${hasError ? 'text-danger' : ''}`}>
                  {metric.name || 'Unnamed metric'}
                </span>
                {hasError && <span className="text-danger text-xs">*</span>}
              </div>
              <div className="text-sm text-default-500">{rangeDisplay}</div>
            </button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleOpenEditModal(metric)}
              aria-label={`Edit ${metric.name || 'metric'}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => handleDeleteMetric(metric.id)}
              aria-label={`Delete ${metric.name || 'metric'}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-stretch gap-3 p-3">
            <DragHandle dragHandleProps={dragHandleProps} />
            <div className="flex-1">
              <Input
                size="sm"
                placeholder="e.g., HRV, Sleep Score"
                value={metric.name}
                onChange={(e) => handleMetricChange(metric.id, 'name', e.target.value)}
                isInvalid={!!errors.name}
                errorMessage={errors.name}
                aria-label="Metric name"
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>
            <div className="w-24">
              <NumberInput
                size="sm"
                placeholder="0"
                value={metric.min}
                onValueChange={(val) => handleMetricChange(metric.id, 'min', val ?? 0)}
                isInvalid={!!errors.min}
                errorMessage={errors.min}
                aria-label="Minimum value"
                hideStepper
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>
            <div className="w-24">
              <NumberInput
                size="sm"
                placeholder="100"
                value={metric.max}
                onValueChange={(val) => handleMetricChange(metric.id, 'max', val ?? 0)}
                isInvalid={!!errors.max}
                errorMessage={errors.max}
                aria-label="Maximum value"
                hideStepper
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>
            <div className="w-20">
              <Input
                size="sm"
                placeholder="e.g., ms"
                value={metric.unit || ''}
                onChange={(e) => handleMetricChange(metric.id, 'unit', e.target.value)}
                aria-label="Unit (optional)"
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => handleDeleteMetric(metric.id)}
              aria-label={`Delete ${metric.name || 'metric'}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      );
    },
    [validationErrors, handleMetricChange, handleDeleteMetric, handleOpenEditModal]
  );

  // Get label for pending source
  const pendingSourceLabel = pendingSource
    ? SOURCE_OPTIONS.find((o) => o.key === pendingSource)?.label ?? pendingSource
    : '';

  return (
    <>
      {/* Main Config Modal */}
      <Modal
        isOpen={isOpen && !showConfirmModal && editingMetricId === null}
        onClose={onClose}
        placement="top"
        scrollBehavior="inside"
        size="2xl"
        isDismissable={!isSaving}
        hideCloseButton={isSaving}
      >
        <ModalContent>
          <ModalHeader>
            <span className="text-lg font-semibold">Configure Health Metrics</span>
          </ModalHeader>
          <ModalBody className="gap-4">
            {/* Divergent State Warning - shows if draft was divergent when modal opened */}
            {wasInitiallyDivergent && (
              <Alert
                color="warning"
                title="This draft differs from your defaults"
                description="Your current edits won't affect this warning."
                endContent={
                  <Button
                    size="sm"
                    variant="flat"
                    color="warning"
                    onPress={handleResetToDefaults}
                  >
                    Reset to defaults
                  </Button>
                }
              />
            )}

            {/* Source Selector */}
            <div className="max-w-xs">
              <Select
                label="Fitness Tracker"
                placeholder="Select source"
                selectedKeys={new Set([localSource])}
                onSelectionChange={handleSourceSelection}
                size="sm"
              >
                {SOURCE_OPTIONS.map((option) => (
                  <SelectItem key={option.key}>{option.label}</SelectItem>
                ))}
              </Select>
            </div>

            {/* Metrics List */}
            {localMetrics.length > 0 ? (
              <div className="space-y-2">
                {/* Column Headers (desktop only) */}
                <div className="hidden md:flex items-center gap-3 px-3 text-sm text-default-500 font-medium">
                  <div className="w-6" />
                  <div className="flex-1 pl-6">Metric</div>
                  <div className="w-24">Min</div>
                  <div className="w-24">Max</div>
                  <div className="w-20">Unit</div>
                  <div className="w-8" />
                </div>
                <SortableList
                  items={localMetrics}
                  onReorder={handleReorder}
                  renderItem={renderMetricRow}
                />
              </div>
            ) : (
              <EmptyState
                title="No health metrics configured"
                description="Add metrics to track your daily wellness before trading."
              />
            )}

            {/* Add Metric Button */}
            <div className="md:hidden">
              <Button
                variant="flat"
                color="primary"
                startContent={<Plus className="w-4 h-4" />}
                onPress={handleOpenAddModal}
                className="w-full"
              >
                Add Metric
              </Button>
            </div>
            <div className="hidden md:block">
              <Button
                variant="flat"
                color="primary"
                startContent={<Plus className="w-4 h-4" />}
                onPress={handleAddMetric}
              >
                Add Metric
              </Button>
            </div>

            {/* Apply to Future Sessions Checkbox - always visible */}
            <Checkbox
              isSelected={applyToFuture}
              onValueChange={setApplyToFuture}
              size="sm"
            >
              Apply to future sessions
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSave} isLoading={isSaving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Source Change Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={handleCancelSourceChange}>
        <ModalContent>
          <ModalHeader>Change Data Source?</ModalHeader>
          <ModalBody>
            <p>
              Switching to <strong>{pendingSourceLabel}</strong> will replace your
              current metrics with the default metrics for that source.
            </p>
            <p className="text-default-500 text-sm mt-2">
              This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelSourceChange}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleConfirmSourceChange}>
              Replace Metrics
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mobile Edit/Add Metric Modal */}
      <Modal
        isOpen={editingMetricId !== null}
        onClose={handleCancelEditModal}
        placement="top"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {editingMetricId === 'new' ? 'Add Metric' : 'Edit Metric'}
          </ModalHeader>
          <ModalBody className="gap-4">
            {editingDraft && (
              <>
                <Input
                  label="Metric Name"
                  placeholder="e.g., HRV, Sleep Score"
                  value={editingDraft.name}
                  onChange={(e) => handleEditDraftChange('name', e.target.value)}
                  isInvalid={!!editModalErrors.name}
                  errorMessage={editModalErrors.name}
                  autoFocus
                  classNames={{ input: 'text-base' }}
                />
                <div className="flex gap-4">
                  <NumberInput
                    label="Min"
                    placeholder="0"
                    value={editingDraft.min}
                    onValueChange={(val) => handleEditDraftChange('min', val ?? 0)}
                    isInvalid={!!editModalErrors.min}
                    errorMessage={editModalErrors.min}
                    hideStepper
                    className="flex-1"
                    classNames={{ input: 'text-base' }}
                  />
                  <NumberInput
                    label="Max"
                    placeholder="100"
                    value={editingDraft.max}
                    onValueChange={(val) => handleEditDraftChange('max', val ?? 0)}
                    isInvalid={!!editModalErrors.max}
                    errorMessage={editModalErrors.max}
                    hideStepper
                    className="flex-1"
                    classNames={{ input: 'text-base' }}
                  />
                </div>
                <Input
                  label="Unit (optional)"
                  placeholder="e.g., ms, %, bpm"
                  value={editingDraft.unit || ''}
                  onChange={(e) => handleEditDraftChange('unit', e.target.value)}
                  classNames={{ input: 'text-base' }}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelEditModal}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSaveEditModal}>
              {editingMetricId === 'new' ? 'Add' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default HealthMetricsConfigModal;
